import {z} from 'zod'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useFieldArray, useFormContext} from 'react-hook-form'
import {useCallback, useEffect} from 'react'
import {useTranslation} from '@/translations/i18n'
import {InputText} from '@/components/commons/input-text/InputText'
import {MedicalReportFieldsSchema} from '../../formSchemas'
import {Divider} from '@/components/ui/divider/Divider'
import {Button} from '@/components/ui/button/Button'
import {PlusIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {StyledTrashButtonContainer} from './style'

const FamilySituationFieldsSchema = MedicalReportFieldsSchema.pick({
    familyMembersSituations: true
})
type FamilySituationFieldsSchema = z.infer<typeof FamilySituationFieldsSchema>

export const FamilySituationFields = () => {
    const {t} = useTranslation()
    const {control, register} = useFormContext<FamilySituationFieldsSchema>()
    const {fields, append, replace, remove} = useFieldArray({name: 'familyMembersSituations', control})

    const handleInitFamilySituationFields = useCallback(() => {
        replace({
            familyMemberAge: '',
            familyMemberFullName: '',
            familyMemberKinship: '',
            familyMemberWorkingSituation: ''
        })
    }, [])

    const handleAddFamilySituationFields = useCallback(() => {
        append({
            familyMemberAge: '',
            familyMemberFullName: '',
            familyMemberKinship: '',
            familyMemberWorkingSituation: ''
        })
    }, [])

    useEffect(() => {
        if (fields.length == 0) {
            handleInitFamilySituationFields()
        }
    }, [fields])

    return (
        <>
            {fields.map((field, index) => (
                <Flexbox key={field.id}>
                    <Grid columns={4} columnGap={2.5} rowGap={2} fullWidth>
                        <GridItem spanColumn={3}>
                            <InputText
                                type="text"
                                label={t('commons:fullname_reverse')}
                                placeholder={t('medical_records:insert_info')}
                                {...register(`familyMembersSituations.${index}.familyMemberFullName`)}
                            />
                        </GridItem>
                        <GridItem spanColumn={1}>
                            <InputText
                                type="number"
                                label={t('commons:age')}
                                placeholder={t('medical_records:insert_info')}
                                {...register(`familyMembersSituations.${index}.familyMemberAge`)}
                            />
                        </GridItem>
                        <GridItem spanColumn={2}>
                            <InputText
                                type="text"
                                label={t('medical_records:kinship_relationship')}
                                placeholder={t('medical_records:insert_info')}
                                {...register(`familyMembersSituations.${index}.familyMemberKinship`)}
                            />
                        </GridItem>
                        <GridItem spanColumn={2}>
                            <InputText
                                type="text"
                                label={t('medical_records:working_situation')}
                                placeholder={t('medical_records:insert_info')}
                                {...register(`familyMembersSituations.${index}.familyMemberWorkingSituation`)}
                            />
                        </GridItem>
                        {index < fields.length - 1 && (
                            <GridItem spanColumn={4}>
                                <Divider direction="horizontal" startSpacing={4} endSpacing={2} />
                            </GridItem>
                        )}
                    </Grid>
                    {index > 0 && (
                        <StyledTrashButtonContainer>
                            <Button shape="square" variant="ghost" onClick={() => remove(index)}>
                                <Trash01Icon />
                            </Button>
                        </StyledTrashButtonContainer>
                    )}
                </Flexbox>
            ))}
            <Divider direction="horizontal" startSpacing={4} endSpacing={2} />
            <Button size="sm" variant="secondary" onClick={handleAddFamilySituationFields}>
                <PlusIcon />
                {t('medical_records:add_cohabitee')}
            </Button>
        </>
    )
}
